/* The internal utility app directory ("All Apps → Utilities"). Every entry is
   a working tool built on one of five engines (transform / generate /
   reference / fields / custom) — see components/apps/utilities/engines.tsx. */
import {
  ArrowDownAZ,
  ArrowLeftRight,
  AtSign,
  BarChart3,
  Binary,
  BookOpen,
  Box,
  Braces,
  Brackets,
  Calculator,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  CaseLower,
  CaseSensitive,
  CaseUpper,
  Clock,
  Code2,
  Columns3,
  Contrast,
  Database,
  Dices,
  Droplet,
  Eye,
  FileCode,
  FileDiff,
  FileText,
  Filter,
  Fingerprint,
  GitBranch,
  Globe,
  Hash,
  Heading1,
  Hourglass,
  Keyboard,
  KeyRound,
  Layers,
  Link2,
  ListFilter,
  ListOrdered,
  ListPlus,
  ListX,
  Lock,
  Merge,
  Network,
  Package,
  Paintbrush,
  Palette,
  Percent,
  Pi,
  Quote,
  Regex,
  Repeat,
  Replace,
  Ruler,
  Scale,
  ScrollText,
  Server,
  Shield,
  Shuffle,
  Sigma,
  Smile,
  Sparkles,
  Split,
  SquareCode,
  Table,
  Terminal,
  Thermometer,
  Timer,
  Type,
  Wand2,
  Watch,
  Wifi,
} from "lucide-react";
import type { UtilityDef } from "@/components/apps/utilities/engines";
import * as F from "@/lib/utilfns";
import {
  CronExplainer,
  DiffViewer,
  FindReplace,
  HashTool,
  HmacTool,
  HtmlPreview,
  KeycodeInspector,
  MarkdownPreview,
  RegenList,
  RegexTester,
  TimestampTool,
  WordCounter,
} from "@/components/apps/utilities/tools";
import {
  BoxShadowGenerator,
  ColorConverter,
  ContrastChecker,
  GradientGenerator,
  PaletteGenerator,
  ShadesGenerator,
} from "@/components/apps/utilities/design-tools";

export const UTILITY_CATEGORIES = [
  "Text",
  "Encode & Decode",
  "Hash & Crypto",
  "Generators",
  "JSON & Data",
  "Time & Date",
  "Numbers & Units",
  "Web & Network",
  "Design & CSS",
  "Dev Tools",
  "Reference",
] as const;

const rowsOut = (pairs: [string, string][]) => pairs.map(([label, value]) => ({ label, value }));

const num = (v: string, label: string): number => {
  const n = Number(v);
  if (v.trim() === "" || !isFinite(n)) throw new Error(`Enter a number for ${label}.`);
  return n;
};

/* Interpret a datetime-local string as wall time in an IANA zone → UTC Date. */
function zonedToUtc(dateStr: string, zone: string): Date {
  const naive = new Date(dateStr);
  if (isNaN(naive.getTime())) throw new Error("Enter a valid date & time.");
  if (zone === "Local") return naive;
  const asZone = new Date(naive.toLocaleString("en-US", { timeZone: zone }));
  return new Date(naive.getTime() + (naive.getTime() - asZone.getTime()));
}

const TIMEZONES = [
  "Local", "UTC", "America/Los_Angeles", "America/Denver", "America/Chicago",
  "America/New_York", "America/Sao_Paulo", "Europe/London", "Europe/Paris",
  "Europe/Berlin", "Europe/Istanbul", "Africa/Cairo", "Asia/Dubai",
  "Asia/Kolkata", "Asia/Singapore", "Asia/Shanghai", "Asia/Tokyo",
  "Australia/Sydney", "Pacific/Auckland",
];

const GITIGNORE_TEMPLATES: Record<string, string> = {
  Node: "node_modules/\ndist/\nbuild/\ncoverage/\n.env\n.env.*\n!.env.example\nnpm-debug.log*\n.DS_Store",
  Python: "__pycache__/\n*.py[cod]\n.venv/\nvenv/\n.env\ndist/\nbuild/\n*.egg-info/\n.pytest_cache/\n.mypy_cache/\n.DS_Store",
  Go: "bin/\n*.exe\n*.test\n*.out\nvendor/\n.env\n.DS_Store",
  Rust: "target/\ndebug/\n*.pdb\n.env\n.DS_Store",
  Java: "target/\nbuild/\n*.class\n*.jar\n.gradle/\n.idea/\n*.iml\n.env\n.DS_Store",
  General: ".DS_Store\nThumbs.db\n*.log\n*.tmp\n.env\n.idea/\n.vscode/",
};

/* ── Reference table data ─────────────────────────────────────────────────── */

const HTTP_STATUSES: string[][] = [
  ["100", "Continue", "Client should continue the request"],
  ["101", "Switching Protocols", "Server agrees to switch (e.g. WebSocket upgrade)"],
  ["200", "OK", "Standard success"],
  ["201", "Created", "Resource created — respond with Location header"],
  ["202", "Accepted", "Queued for async processing"],
  ["204", "No Content", "Success with an empty body"],
  ["206", "Partial Content", "Range request response"],
  ["301", "Moved Permanently", "Permanent redirect — method may change to GET"],
  ["302", "Found", "Temporary redirect — method may change to GET"],
  ["304", "Not Modified", "Conditional GET — use the cached copy"],
  ["307", "Temporary Redirect", "Temporary — method preserved"],
  ["308", "Permanent Redirect", "Permanent — method preserved"],
  ["400", "Bad Request", "Malformed syntax or invalid parameters"],
  ["401", "Unauthorized", "Missing or invalid authentication"],
  ["403", "Forbidden", "Authenticated but not allowed"],
  ["404", "Not Found", "Resource doesn't exist"],
  ["405", "Method Not Allowed", "Method not supported on this resource"],
  ["406", "Not Acceptable", "Can't satisfy Accept headers"],
  ["408", "Request Timeout", "Client took too long to send the request"],
  ["409", "Conflict", "State conflict (e.g. version mismatch)"],
  ["410", "Gone", "Permanently removed"],
  ["412", "Precondition Failed", "If-Match / If-Unmodified-Since failed"],
  ["413", "Payload Too Large", "Body exceeds server limits"],
  ["415", "Unsupported Media Type", "Content-Type not supported"],
  ["418", "I'm a teapot", "RFC 2324 — april fools, but real servers send it"],
  ["422", "Unprocessable Entity", "Validation failed on a well-formed request"],
  ["425", "Too Early", "Server unwilling to risk a replayed request"],
  ["428", "Precondition Required", "Server requires a conditional request"],
  ["429", "Too Many Requests", "Rate limited — check Retry-After"],
  ["451", "Unavailable For Legal Reasons", "Blocked for legal/compliance reasons"],
  ["500", "Internal Server Error", "Unhandled server-side failure"],
  ["501", "Not Implemented", "Method not implemented by the server"],
  ["502", "Bad Gateway", "Upstream returned an invalid response"],
  ["503", "Service Unavailable", "Overloaded or down for maintenance"],
  ["504", "Gateway Timeout", "Upstream didn't respond in time"],
  ["507", "Insufficient Storage", "Server out of space"],
];

const HTTP_HEADERS: string[][] = [
  ["Authorization", "Request", "Credentials — Bearer token, Basic auth, etc."],
  ["Accept", "Request", "Media types the client can handle"],
  ["Accept-Encoding", "Request", "Compression the client supports (gzip, br)"],
  ["Content-Type", "Both", "Media type of the body"],
  ["Content-Length", "Both", "Body size in bytes"],
  ["Content-Encoding", "Response", "Compression applied to the body"],
  ["Cache-Control", "Both", "Caching directives (max-age, no-store, …)"],
  ["ETag", "Response", "Version identifier for conditional requests"],
  ["If-None-Match", "Request", "Conditional GET against an ETag"],
  ["If-Match", "Request", "Optimistic-concurrency write guard"],
  ["Last-Modified", "Response", "Timestamp for conditional requests"],
  ["Location", "Response", "Redirect target / created resource URL"],
  ["Set-Cookie", "Response", "Store a cookie on the client"],
  ["Cookie", "Request", "Cookies previously set"],
  ["Origin", "Request", "Origin of a cross-site request"],
  ["Access-Control-Allow-Origin", "Response", "CORS: allowed origins"],
  ["Access-Control-Allow-Methods", "Response", "CORS: allowed methods on preflight"],
  ["Access-Control-Allow-Headers", "Response", "CORS: allowed request headers"],
  ["Referer", "Request", "URL of the referring page (yes, misspelled)"],
  ["User-Agent", "Request", "Client software identifier"],
  ["Host", "Request", "Target host and port — required in HTTP/1.1"],
  ["Retry-After", "Response", "When to retry (429 / 503)"],
  ["Strict-Transport-Security", "Response", "Force HTTPS for future requests"],
  ["Content-Security-Policy", "Response", "Restrict resource loading (XSS defense)"],
  ["X-Content-Type-Options", "Response", "nosniff — disable MIME sniffing"],
  ["X-Frame-Options", "Response", "Clickjacking protection (DENY / SAMEORIGIN)"],
  ["X-Request-Id", "Both", "Correlation id for tracing (convention)"],
  ["X-Forwarded-For", "Request", "Original client IP through proxies"],
  ["Forwarded", "Request", "Standardized proxy chain info"],
  ["Range", "Request", "Request part of a resource (bytes=0-1023)"],
  ["Transfer-Encoding", "Response", "chunked — streaming without Content-Length"],
  ["Vary", "Response", "Request headers that affect the cached response"],
  ["WWW-Authenticate", "Response", "Auth challenge accompanying a 401"],
];

const MIME_TYPES: string[][] = [
  [".json", "application/json", "JSON"],
  [".html", "text/html", "HTML document"],
  [".css", "text/css", "Stylesheet"],
  [".js / .mjs", "text/javascript", "JavaScript"],
  [".ts", "video/mp2t (careful!)", "TypeScript files have no registered type"],
  [".txt", "text/plain", "Plain text"],
  [".csv", "text/csv", "Comma-separated values"],
  [".xml", "application/xml", "XML"],
  [".yaml / .yml", "application/yaml", "YAML (RFC 9512)"],
  [".pdf", "application/pdf", "PDF document"],
  [".zip", "application/zip", "ZIP archive"],
  [".gz", "application/gzip", "Gzip archive"],
  [".tar", "application/x-tar", "Tarball"],
  [".png", "image/png", "PNG image"],
  [".jpg / .jpeg", "image/jpeg", "JPEG image"],
  [".gif", "image/gif", "GIF image"],
  [".webp", "image/webp", "WebP image"],
  [".avif", "image/avif", "AVIF image"],
  [".svg", "image/svg+xml", "SVG vector image"],
  [".ico", "image/x-icon", "Favicon"],
  [".mp3", "audio/mpeg", "MP3 audio"],
  [".wav", "audio/wav", "WAV audio"],
  [".ogg", "audio/ogg", "Ogg audio"],
  [".mp4", "video/mp4", "MP4 video"],
  [".webm", "video/webm", "WebM video"],
  [".woff2", "font/woff2", "Web font"],
  [".woff", "font/woff", "Web font (legacy)"],
  [".ttf", "font/ttf", "TrueType font"],
  [".wasm", "application/wasm", "WebAssembly module"],
  [".md", "text/markdown", "Markdown"],
  ["form upload", "multipart/form-data", "File uploads"],
  ["form post", "application/x-www-form-urlencoded", "Classic form body"],
  ["binary", "application/octet-stream", "Arbitrary bytes / force download"],
  ["events", "text/event-stream", "Server-sent events"],
  ["ndjson", "application/x-ndjson", "Newline-delimited JSON streams"],
];

const COMMON_PORTS: string[][] = [
  ["20/21", "FTP", "File transfer (data/control)"],
  ["22", "SSH", "Secure shell, SFTP, port forwarding"],
  ["23", "Telnet", "Legacy remote shell — avoid"],
  ["25", "SMTP", "Mail relay between servers"],
  ["53", "DNS", "Name resolution (UDP + TCP)"],
  ["67/68", "DHCP", "Address assignment"],
  ["80", "HTTP", "Unencrypted web traffic"],
  ["110", "POP3", "Mail retrieval (legacy)"],
  ["123", "NTP", "Time sync"],
  ["143", "IMAP", "Mail retrieval"],
  ["443", "HTTPS", "TLS web traffic"],
  ["465/587", "SMTPS / Submission", "Authenticated mail submission"],
  ["993", "IMAPS", "IMAP over TLS"],
  ["995", "POP3S", "POP3 over TLS"],
  ["1433", "SQL Server", "Microsoft SQL Server"],
  ["1521", "Oracle", "Oracle database listener"],
  ["2049", "NFS", "Network file system"],
  ["27017", "MongoDB", "MongoDB default"],
  ["3000", "Dev server", "Rails, Express, Grafana default"],
  ["3306", "MySQL", "MySQL / MariaDB"],
  ["3389", "RDP", "Windows remote desktop"],
  ["4222", "NATS", "NATS messaging"],
  ["5173", "Vite", "Vite dev server default"],
  ["5432", "PostgreSQL", "Postgres default"],
  ["5672", "AMQP", "RabbitMQ"],
  ["6379", "Redis", "Redis default"],
  ["8080", "HTTP alt", "Common app/proxy port"],
  ["8443", "HTTPS alt", "Common TLS alt port"],
  ["9000", "MinIO / PHP-FPM", "Object storage & FPM default"],
  ["9090", "Prometheus", "Metrics server"],
  ["9092", "Kafka", "Kafka broker"],
  ["11211", "Memcached", "In-memory cache"],
];

const DNS_RECORDS: string[][] = [
  ["A", "IPv4 address for a hostname"],
  ["AAAA", "IPv6 address for a hostname"],
  ["CNAME", "Alias to another hostname (no other records allowed on the name)"],
  ["MX", "Mail server + priority for a domain"],
  ["TXT", "Arbitrary text — SPF, DKIM, domain verification"],
  ["NS", "Authoritative name servers for the zone"],
  ["SOA", "Zone metadata: primary NS, serial, refresh timers"],
  ["SRV", "Service locator: host + port for a protocol"],
  ["PTR", "Reverse lookup: IP → hostname"],
  ["CAA", "Which CAs may issue certificates for the domain"],
  ["DNSKEY / DS", "DNSSEC signing keys and delegation digests"],
  ["ALIAS / ANAME", "CNAME-like at the zone apex (provider-specific)"],
  ["HTTPS / SVCB", "Endpoint parameters — ALPN, ECH, IP hints"],
  ["NAPTR", "Regex-based rewriting (SIP, ENUM)"],
];

const REGEX_CHEATSHEET: string[][] = [
  [".", "Any character except newline"],
  ["\\d  \\D", "Digit / non-digit"],
  ["\\w  \\W", "Word char [A-Za-z0-9_] / non-word"],
  ["\\s  \\S", "Whitespace / non-whitespace"],
  ["\\b  \\B", "Word boundary / non-boundary"],
  ["^  $", "Start / end of line (with m flag: each line)"],
  ["[abc]  [^abc]", "Character class / negated class"],
  ["[a-z0-9]", "Ranges inside a class"],
  ["a*  a+  a?", "0+ / 1+ / 0-1 (greedy)"],
  ["a*?  a+?", "Lazy quantifiers"],
  ["a{2}  a{2,}  a{2,5}", "Exactly / at least / between"],
  ["(x)", "Capture group"],
  ["(?:x)", "Non-capturing group"],
  ["(?<name>x)", "Named capture group"],
  ["\\1  \\k<name>", "Backreferences"],
  ["x|y", "Alternation"],
  ["(?=x)  (?!x)", "Lookahead / negative lookahead"],
  ["(?<=x)  (?<!x)", "Lookbehind / negative lookbehind"],
  ["flags: g i m s u y", "global · ignore case · multiline · dotall · unicode · sticky"],
  ["\\p{L} (u flag)", "Unicode property escapes — any letter"],
];

const GIT_CHEATSHEET: string[][] = [
  ["git status -sb", "Compact status with branch info"],
  ["git switch -c feat/x", "Create and switch to a branch"],
  ["git add -p", "Stage hunks interactively"],
  ["git commit --amend", "Rewrite the last commit"],
  ["git commit --fixup <sha>", "Mark a commit as a fix for autosquash"],
  ["git rebase -i --autosquash main", "Interactive rebase folding fixups"],
  ["git rebase --onto A B C", "Transplant C's commits after B onto A"],
  ["git cherry-pick <sha>", "Apply one commit onto the current branch"],
  ["git stash push -m 'wip'", "Shelve working tree changes"],
  ["git stash pop", "Restore the latest stash"],
  ["git log --oneline --graph --all", "Visual history of all branches"],
  ["git log -S 'needle'", "Find commits adding/removing a string"],
  ["git log -L :func:file.ts", "History of one function"],
  ["git blame -w -C file", "Blame ignoring whitespace & copies"],
  ["git bisect start/good/bad", "Binary-search for the breaking commit"],
  ["git diff --staged", "What's about to be committed"],
  ["git diff main...HEAD", "Changes since branching off main"],
  ["git restore --staged f", "Unstage a file"],
  ["git restore f", "Discard working-tree changes to a file"],
  ["git reset --soft HEAD~1", "Undo commit, keep changes staged"],
  ["git reset --hard origin/main", "Match remote exactly (destructive)"],
  ["git revert <sha>", "New commit that undoes an old one"],
  ["git reflog", "Recover 'lost' commits and branches"],
  ["git worktree add ../wt branch", "Second working copy of the repo"],
  ["git fetch --prune", "Sync refs, drop deleted remote branches"],
  ["git pull --rebase", "Update without a merge commit"],
  ["git push --force-with-lease", "Safer force push"],
  ["git tag -a v1.2.0 -m 'msg'", "Annotated release tag"],
  ["git clean -nd / -fd", "Preview / delete untracked files"],
  ["git shortlog -sn", "Commit counts per author"],
];

const NPM_COMMANDS: string[][] = [
  ["npm install", "yarn", "pnpm install"],
  ["npm install <pkg>", "yarn add <pkg>", "pnpm add <pkg>"],
  ["npm install -D <pkg>", "yarn add -D <pkg>", "pnpm add -D <pkg>"],
  ["npm install -g <pkg>", "yarn global add <pkg>", "pnpm add -g <pkg>"],
  ["npm uninstall <pkg>", "yarn remove <pkg>", "pnpm remove <pkg>"],
  ["npm run build", "yarn build", "pnpm build"],
  ["npm test", "yarn test", "pnpm test"],
  ["npx <cmd>", "yarn dlx <cmd>", "pnpm dlx <cmd>"],
  ["npm outdated", "yarn outdated", "pnpm outdated"],
  ["npm update", "yarn upgrade", "pnpm update"],
  ["npm audit", "yarn audit", "pnpm audit"],
  ["npm ci", "yarn install --frozen-lockfile", "pnpm install --frozen-lockfile"],
  ["npm ls <pkg>", "yarn why <pkg>", "pnpm why <pkg>"],
  ["npm link", "yarn link", "pnpm link"],
  ["npm publish", "yarn publish", "pnpm publish"],
  ["npm init", "yarn init", "pnpm init"],
];

const DOCKER_CHEATSHEET: string[][] = [
  ["docker ps -a", "List containers (including stopped)"],
  ["docker images", "List local images"],
  ["docker build -t app:latest .", "Build an image from the Dockerfile"],
  ["docker run -d -p 8080:80 app", "Run detached with a port mapping"],
  ["docker run -it app sh", "Interactive shell in a fresh container"],
  ["docker exec -it <ctr> sh", "Shell into a running container"],
  ["docker logs -f <ctr>", "Follow container logs"],
  ["docker stop / start <ctr>", "Stop / start a container"],
  ["docker rm <ctr> · rmi <img>", "Remove container / image"],
  ["docker system prune -a", "Delete unused containers, images, networks"],
  ["docker volume ls / inspect", "Inspect volumes"],
  ["docker network ls", "List networks"],
  ["docker cp <ctr>:/path .", "Copy files out of a container"],
  ["docker inspect <ctr>", "Full JSON config of a container"],
  ["docker stats", "Live CPU/memory per container"],
  ["docker compose up -d", "Start the stack in the background"],
  ["docker compose down -v", "Stop and remove volumes too"],
  ["docker compose logs -f svc", "Follow one service's logs"],
  ["docker compose exec svc sh", "Shell into a compose service"],
  ["docker tag app reg/app:1.0", "Tag for a registry"],
  ["docker push reg/app:1.0", "Push to a registry"],
];

const BASH_SHORTCUTS: string[][] = [
  ["Ctrl+R", "Reverse-search command history"],
  ["Ctrl+A / Ctrl+E", "Jump to start / end of line"],
  ["Alt+B / Alt+F", "Move back / forward one word"],
  ["Ctrl+W", "Delete word before cursor"],
  ["Alt+D", "Delete word after cursor"],
  ["Ctrl+U / Ctrl+K", "Delete to start / end of line"],
  ["Ctrl+Y", "Paste last deleted text (yank)"],
  ["Ctrl+L", "Clear screen (keeps current line)"],
  ["Ctrl+C", "Cancel current command"],
  ["Ctrl+D", "EOF / exit shell"],
  ["Ctrl+Z", "Suspend to background (fg to resume)"],
  ["!!", "Repeat last command (sudo !!)"],
  ["!$  /  Alt+.", "Last argument of previous command"],
  ["^old^new", "Rerun last command with a substitution"],
  ["cd -", "Jump to previous directory"],
  ["{a,b}", "Brace expansion: mv file.{js,ts}"],
  ["ctrl+x ctrl+e", "Edit current line in $EDITOR"],
  ["set -euo pipefail", "Strict mode for scripts"],
];

const MARKDOWN_CHEATSHEET: string[][] = [
  ["# H1 … ###### H6", "Headings"],
  ["**bold**  *italic*", "Emphasis"],
  ["~~strikethrough~~", "Strikethrough (GFM)"],
  ["`inline code`", "Inline code"],
  ["```lang … ```", "Fenced code block with highlighting"],
  ["- item / 1. item", "Unordered / ordered lists"],
  ["- [ ] task", "Task list (GFM)"],
  ["[text](url)", "Link"],
  ["![alt](url)", "Image"],
  ["> quote", "Blockquote"],
  ["---", "Horizontal rule"],
  ["| a | b |", "Table (GFM) — second row: | --- | --- |"],
  ["  (two spaces at EOL)", "Hard line break"],
  ["\\*literal\\*", "Escape formatting characters"],
  ["<details><summary>", "Collapsible section (HTML passthrough)"],
  ["footnote[^1]", "Footnotes (GFM extension)"],
];

const HTML_ENTITIES_REF: string[][] = [
  ["&amp;", "&", "Ampersand"],
  ["&lt; &gt;", "< >", "Angle brackets"],
  ["&quot; &#39;", '" \'', "Quotes"],
  ["&nbsp;", "␣", "Non-breaking space"],
  ["&mdash; &ndash;", "— –", "Em / en dash"],
  ["&hellip;", "…", "Ellipsis"],
  ["&copy; &reg; &trade;", "© ® ™", "Legal marks"],
  ["&deg;", "°", "Degree"],
  ["&plusmn;", "±", "Plus-minus"],
  ["&times; &divide;", "× ÷", "Multiply / divide"],
  ["&le; &ge; &ne;", "≤ ≥ ≠", "Comparisons"],
  ["&larr; &rarr; &uarr; &darr;", "← → ↑ ↓", "Arrows"],
  ["&bull; &middot;", "• ·", "Bullets"],
  ["&euro; &pound; &yen; &cent;", "€ £ ¥ ¢", "Currency"],
  ["&alpha; &beta; &lambda; &pi;", "α β λ π", "Greek letters"],
  ["&check; &cross;", "✓ ✗", "Check / cross"],
  ["&sect; &para;", "§ ¶", "Section / paragraph"],
  ["&laquo; &raquo;", "« »", "Guillemets"],
];

const SEMVER_REF: string[][] = [
  ["1.2.3", "major.minor.patch"],
  ["^1.2.3", ">=1.2.3 <2.0.0 — default npm range"],
  ["~1.2.3", ">=1.2.3 <1.3.0 — patch updates only"],
  ["^0.2.3", ">=0.2.3 <0.3.0 — caret is stricter below 1.0"],
  ["^0.0.3", "Exactly 0.0.3 — 0.0.x caret pins"],
  ["1.2.x / 1.2.*", ">=1.2.0 <1.3.0"],
  [">=1.2 <2", "Explicit range"],
  ["1.2.3 - 2.3.4", "Hyphen range (inclusive)"],
  ["1.2.3-beta.1", "Prerelease — lower precedence than 1.2.3"],
  ["1.2.3+build.5", "Build metadata — ignored for precedence"],
  ["*", "Any version (avoid in dependencies)"],
  ["latest / next", "npm dist-tags, not semver"],
];

const LICENSE_REF: string[][] = [
  ["MIT", "Permissive", "Do anything; keep the notice. De-facto default"],
  ["Apache-2.0", "Permissive", "MIT-like + explicit patent grant"],
  ["BSD-3-Clause", "Permissive", "MIT-like + no-endorsement clause"],
  ["ISC", "Permissive", "Simplified MIT equivalent"],
  ["MPL-2.0", "Weak copyleft", "Modified files stay open; can combine with closed code"],
  ["LGPL-3.0", "Weak copyleft", "Library copyleft — dynamic linking OK"],
  ["GPL-3.0", "Strong copyleft", "Derivatives must be GPL"],
  ["AGPL-3.0", "Strong copyleft", "GPL that also triggers over a network (SaaS)"],
  ["Unlicense / CC0", "Public domain", "No rights reserved"],
  ["BUSL-1.1", "Source-available", "Not OSI open source; converts after a term"],
  ["proprietary", "Closed", "All rights reserved"],
];

const EMOJI_REF: string[][] = [
  ["✅", ":white_check_mark:", "Done / approved"],
  ["❌", ":x:", "Failed / rejected"],
  ["⚠️", ":warning:", "Caution"],
  ["🚀", ":rocket:", "Ship it / deploy"],
  ["🐛", ":bug:", "Bug"],
  ["🔥", ":fire:", "Hot / removing code"],
  ["✨", ":sparkles:", "New feature"],
  ["🎉", ":tada:", "Celebration / initial commit"],
  ["🔧", ":wrench:", "Config / tooling"],
  ["🔒", ":lock:", "Security"],
  ["📝", ":memo:", "Docs"],
  ["♻️", ":recycle:", "Refactor"],
  ["⚡", ":zap:", "Performance"],
  ["🧪", ":test_tube:", "Tests"],
  ["🚧", ":construction:", "Work in progress"],
  ["⏪", ":rewind:", "Revert"],
  ["📦", ":package:", "Dependencies / build"],
  ["💡", ":bulb:", "Idea"],
  ["👀", ":eyes:", "Reviewing / attention"],
  ["🙏", ":pray:", "Please / thanks"],
  ["👍", ":+1:", "Approve"],
  ["🤝", ":handshake:", "Agreement"],
  ["🧵", ":thread:", "Thread"],
  ["📌", ":pushpin:", "Pinned"],
  ["🗑️", ":wastebasket:", "Deprecated / removal"],
  ["🩹", ":adhesive_bandage:", "Hotfix"],
  ["⏳", ":hourglass:", "Waiting"],
  ["🔁", ":repeat:", "Retry / loop"],
  ["🎯", ":dart:", "Goal / target"],
  ["🧠", ":brain:", "Smart / AI"],
];

const FONT_STACKS: string[][] = [
  ["System UI", "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif"],
  ["Humanist sans", "Seravek, 'Gill Sans Nova', Ubuntu, Calibri, sans-serif"],
  ["Geometric sans", "Avenir, Montserrat, Corbel, 'URW Gothic', sans-serif"],
  ["Classic serif", "Georgia, 'Times New Roman', Times, serif"],
  ["Transitional serif", "Charter, 'Bitstream Charter', 'Sitka Text', Cambria, serif"],
  ["Slab serif", "Rockwell, 'Rockwell Nova', 'Roboto Slab', serif"],
  ["Monospace (code)", "ui-monospace, 'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace"],
  ["Rounded", "ui-rounded, 'Hiragino Maru Gothic ProN', Quicksand, Comfortaa, sans-serif"],
];

const TAILWIND_COLORS: string[][] = [
  ["slate", "#64748b"], ["gray", "#6b7280"], ["zinc", "#71717a"],
  ["neutral", "#737373"], ["stone", "#78716c"], ["red", "#ef4444"],
  ["orange", "#f97316"], ["amber", "#f59e0b"], ["yellow", "#eab308"],
  ["lime", "#84cc16"], ["green", "#22c55e"], ["emerald", "#10b981"],
  ["teal", "#14b8a6"], ["cyan", "#06b6d4"], ["sky", "#0ea5e9"],
  ["blue", "#3b82f6"], ["indigo", "#6366f1"], ["violet", "#8b5cf6"],
  ["purple", "#a855f7"], ["fuchsia", "#d946ef"], ["pink", "#ec4899"],
  ["rose", "#f43f5e"],
];

const ASCII_ROWS: string[][] = Array.from({ length: 95 }, (_, i) => {
  const code = i + 32;
  const ch = code === 32 ? "␠ (space)" : String.fromCharCode(code);
  return [String(code), "0x" + code.toString(16).padStart(2, "0"), ch];
});

/* ── The catalog ──────────────────────────────────────────────────────────── */

export const UTILITIES: UtilityDef[] = [
  /* ── Text ── */
  { id: "uppercase", name: "UPPERCASE", tagline: "Convert text to upper case", category: "Text", icon: CaseUpper, engine: { kind: "transform", fn: (s) => s.toUpperCase() } },
  { id: "lowercase", name: "lowercase", tagline: "Convert text to lower case", category: "Text", icon: CaseLower, engine: { kind: "transform", fn: (s) => s.toLowerCase() } },
  { id: "title-case", name: "Title Case", tagline: "Capitalize Every Word", category: "Text", icon: Heading1, engine: { kind: "transform", fn: F.toTitleCase } },
  { id: "sentence-case", name: "Sentence case", tagline: "Capitalize sentence starts", category: "Text", icon: Type, engine: { kind: "transform", fn: F.toSentenceCase } },
  { id: "camel-case", name: "camelCase", tagline: "toCamelCase identifiers", category: "Text", icon: CaseSensitive, engine: { kind: "transform", fn: F.toCamelCase, sample: "workflow builder test run" } },
  { id: "pascal-case", name: "PascalCase", tagline: "ToPascalCase identifiers", category: "Text", icon: CaseSensitive, engine: { kind: "transform", fn: F.toPascalCase, sample: "workflow builder test run" } },
  { id: "snake-case", name: "snake_case", tagline: "to_snake_case identifiers", category: "Text", icon: CaseSensitive, engine: { kind: "transform", fn: F.toSnakeCase, sample: "WorkflowBuilderTestRun" } },
  { id: "kebab-case", name: "kebab-case", tagline: "to-kebab-case identifiers", category: "Text", icon: CaseSensitive, engine: { kind: "transform", fn: F.toKebabCase, sample: "WorkflowBuilderTestRun" } },
  { id: "constant-case", name: "CONSTANT_CASE", tagline: "TO_CONSTANT_CASE identifiers", category: "Text", icon: CaseSensitive, engine: { kind: "transform", fn: F.toConstantCase, sample: "maxRetryCount" } },
  { id: "slugify", name: "Slugify", tagline: "URL-safe slugs from any text", category: "Text", icon: Link2, engine: { kind: "transform", fn: F.slugify, sample: "Hive: Fix the Hooks — Window (v2)!" } },
  { id: "reverse-text", name: "Reverse Text", tagline: "Mirror a string", category: "Text", icon: ArrowLeftRight, engine: { kind: "transform", fn: (s) => [...s].reverse().join("") } },
  { id: "trim-whitespace", name: "Trim Whitespace", tagline: "Trim lines, collapse runs of spaces", category: "Text", icon: Wand2, engine: { kind: "transform", fn: (s) => F.lines(s).map((l) => l.trim().replace(/\s+/g, " ")).join("\n") } },
  { id: "remove-blank-lines", name: "Remove Blank Lines", tagline: "Drop empty lines from text", category: "Text", icon: ListX, engine: { kind: "transform", fn: (s) => F.lines(s).filter((l) => l.trim() !== "").join("\n") } },
  {
    id: "sort-lines", name: "Sort Lines", tagline: "Sort lines A→Z, Z→A or numerically", category: "Text", icon: ArrowDownAZ,
    engine: {
      kind: "transform",
      options: [
        { key: "order", label: "Order", type: "select", choices: ["A → Z", "Z → A"], default: "A → Z" },
        { key: "numeric", label: "Numeric", type: "checkbox", default: false },
        { key: "ci", label: "Ignore case", type: "checkbox", default: true },
      ],
      fn: (s, o) => {
        const arr = F.lines(s).sort((a, b) =>
          o.numeric ? Number(a) - Number(b) : o.ci ? a.toLowerCase().localeCompare(b.toLowerCase()) : a.localeCompare(b)
        );
        return (o.order === "Z → A" ? arr.reverse() : arr).join("\n");
      },
    },
  },
  {
    id: "dedupe-lines", name: "Dedupe Lines", tagline: "Remove duplicate lines, keep order", category: "Text", icon: ListFilter,
    engine: {
      kind: "transform",
      options: [{ key: "ci", label: "Ignore case", type: "checkbox", default: false }],
      fn: (s, o) => {
        const seen = new Set<string>();
        return F.lines(s).filter((l) => {
          const k = o.ci ? l.toLowerCase() : l;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        }).join("\n");
      },
    },
  },
  { id: "shuffle-lines", name: "Shuffle Lines", tagline: "Randomize line order", category: "Text", icon: Shuffle, engine: { kind: "transform", fn: (s) => F.lines(s).map((l) => [F.randomInt(0, 1e9), l] as const).sort((a, b) => a[0] - b[0]).map(([, l]) => l).join("\n") } },
  {
    id: "number-lines", name: "Number Lines", tagline: "Prefix every line with its number", category: "Text", icon: ListOrdered,
    engine: {
      kind: "transform",
      options: [{ key: "start", label: "Start at", type: "number", default: 1 }],
      fn: (s, o) => F.lines(s).map((l, i) => `${Number(o.start) + i}. ${l}`).join("\n"),
    },
  },
  {
    id: "prefix-suffix", name: "Prefix / Suffix Lines", tagline: "Wrap every line with text", category: "Text", icon: ListPlus,
    engine: {
      kind: "transform",
      options: [
        { key: "prefix", label: "Prefix", type: "text", default: "- " },
        { key: "suffix", label: "Suffix", type: "text", default: "" },
      ],
      fn: (s, o) => F.lines(s).map((l) => `${o.prefix}${l}${o.suffix}`).join("\n"),
    },
  },
  {
    id: "join-lines", name: "Join Lines", tagline: "Lines → one delimited string", category: "Text", icon: Merge,
    engine: {
      kind: "transform",
      options: [{ key: "sep", label: "Separator", type: "text", default: ", " }],
      fn: (s, o) => F.lines(s).filter((l) => l.trim() !== "").join(String(o.sep)),
    },
  },
  {
    id: "split-text", name: "Split to Lines", tagline: "Delimited string → lines", category: "Text", icon: Split,
    engine: {
      kind: "transform",
      options: [{ key: "sep", label: "Separator", type: "text", default: "," }],
      fn: (s, o) => s.split(String(o.sep)).map((p) => p.trim()).filter(Boolean).join("\n"),
    },
  },
  {
    id: "extract-column", name: "Extract Column", tagline: "Pull one column out of delimited text", category: "Text", icon: Columns3,
    engine: {
      kind: "transform",
      options: [
        { key: "sep", label: "Delimiter", type: "text", default: "," },
        { key: "col", label: "Column #", type: "number", default: 1, min: 1 },
      ],
      fn: (s, o) => F.lines(s).map((l) => l.split(String(o.sep))[Number(o.col) - 1]?.trim() ?? "").join("\n"),
    },
  },
  {
    id: "filter-lines", name: "Filter Lines", tagline: "Keep or drop lines matching text", category: "Text", icon: Filter,
    engine: {
      kind: "transform",
      options: [
        { key: "needle", label: "Contains", type: "text", default: "" },
        { key: "mode", label: "Mode", type: "select", choices: ["keep", "remove"], default: "keep" },
      ],
      fn: (s, o) => {
        const needle = String(o.needle).toLowerCase();
        return F.lines(s).filter((l) => l.toLowerCase().includes(needle) === (o.mode === "keep")).join("\n");
      },
    },
  },
  { id: "extract-emails", name: "Extract Emails", tagline: "Pull email addresses out of text", category: "Text", icon: AtSign, engine: { kind: "transform", fn: (s) => [...new Set(s.match(/[\w.+-]+@[\w-]+\.[\w.-]+/g) ?? [])].join("\n") || "(no emails found)" } },
  { id: "extract-urls", name: "Extract URLs", tagline: "Pull links out of text", category: "Text", icon: Link2, engine: { kind: "transform", fn: (s) => [...new Set(s.match(/https?:\/\/[^\s"'<>)\]]+/g) ?? [])].join("\n") || "(no URLs found)" } },
  { id: "char-frequency", name: "Character Frequency", tagline: "Count occurrences of each character", category: "Text", icon: BarChart3, engine: { kind: "transform", fn: F.charFrequency } },
  { id: "word-counter", name: "Word Counter", tagline: "Words, chars, sentences, read time", category: "Text", icon: Sigma, engine: { kind: "custom", component: WordCounter } },
  { id: "find-replace", name: "Find & Replace", tagline: "Replace text with plain or regex search", category: "Text", icon: Replace, engine: { kind: "custom", component: FindReplace } },
  { id: "lorem-ipsum", name: "Lorem Ipsum", tagline: "Placeholder paragraphs", category: "Text", icon: FileText, engine: { kind: "generate", options: [{ key: "n", label: "Paragraphs", type: "number", default: 3, min: 1, max: 20 }], fn: (o) => F.loremIpsum(Number(o.n)) } },

  /* ── Encode & Decode ── */
  { id: "base64-encode", name: "Base64 Encode", tagline: "Text → Base64 (UTF-8 safe)", category: "Encode & Decode", icon: Binary, engine: { kind: "transform", fn: (s) => F.b64encode(s) } },
  { id: "base64-decode", name: "Base64 Decode", tagline: "Base64 → text (UTF-8 safe)", category: "Encode & Decode", icon: Binary, engine: { kind: "transform", fn: F.b64decode, sample: "SGVsbG8gSGl2ZSDwn5Cd" } },
  { id: "base64url", name: "Base64 URL-safe", tagline: "JWT-style Base64 without + / =", category: "Encode & Decode", icon: Binary, engine: { kind: "transform", options: [{ key: "mode", label: "Mode", type: "select", choices: ["encode", "decode"], default: "encode" }], fn: (s, o) => (o.mode === "encode" ? F.b64encode(s, true) : F.b64decode(s)) } },
  { id: "url-encode", name: "URL Encode", tagline: "Percent-encode a string", category: "Encode & Decode", icon: Globe, engine: { kind: "transform", fn: (s) => encodeURIComponent(s) } },
  { id: "url-decode", name: "URL Decode", tagline: "Decode percent-encoding", category: "Encode & Decode", icon: Globe, engine: { kind: "transform", fn: (s) => { try { return decodeURIComponent(s.replace(/\+/g, "%20")); } catch { throw new Error("Malformed percent-encoding."); } } } },
  { id: "html-encode", name: "HTML Entities Encode", tagline: "Escape < > & \" '", category: "Encode & Decode", icon: Code2, engine: { kind: "transform", fn: F.htmlEncode, sample: '<a href="/x?a=1&b=2">link</a>' } },
  { id: "html-decode", name: "HTML Entities Decode", tagline: "&amp;lt; → <", category: "Encode & Decode", icon: Code2, engine: { kind: "transform", fn: F.htmlDecode, sample: "&lt;div class=&quot;hero&quot;&gt;&amp;nbsp;&lt;/div&gt;" } },
  { id: "hex-encode", name: "Hex Encode", tagline: "Text → hexadecimal bytes", category: "Encode & Decode", icon: Hash, engine: { kind: "transform", fn: F.hexEncode } },
  { id: "hex-decode", name: "Hex Decode", tagline: "Hexadecimal bytes → text", category: "Encode & Decode", icon: Hash, engine: { kind: "transform", fn: F.hexDecode, sample: "48 69 76 65 20 f0 9f 90 9d" } },
  { id: "binary-encode", name: "Binary Encode", tagline: "Text → 8-bit binary", category: "Encode & Decode", icon: Binary, engine: { kind: "transform", fn: F.binaryEncode } },
  { id: "binary-decode", name: "Binary Decode", tagline: "8-bit binary → text", category: "Encode & Decode", icon: Binary, engine: { kind: "transform", fn: F.binaryDecode, sample: "01001000 01101001" } },
  { id: "rot13", name: "ROT13", tagline: "Classic letter rotation (self-inverse)", category: "Encode & Decode", icon: Repeat, engine: { kind: "transform", fn: F.rot13, sample: "Uryyb sebz gur Uvir qrfxgbc" } },
  { id: "caesar-cipher", name: "Caesar Cipher", tagline: "Shift letters by N positions", category: "Encode & Decode", icon: Lock, engine: { kind: "transform", options: [{ key: "shift", label: "Shift", type: "number", default: 3, min: -25, max: 25 }, { key: "dir", label: "Direction", type: "select", choices: ["encode", "decode"], default: "encode" }], fn: (s, o) => F.caesar(s, o.dir === "encode" ? Number(o.shift) : -Number(o.shift)) } },
  { id: "morse-encode", name: "Morse Encode", tagline: "Text → dots & dashes", category: "Encode & Decode", icon: Wifi, engine: { kind: "transform", fn: F.morseEncode, sample: "hello hive" } },
  { id: "morse-decode", name: "Morse Decode", tagline: "Dots & dashes → text ( / between words)", category: "Encode & Decode", icon: Wifi, engine: { kind: "transform", fn: F.morseDecode, sample: ".... .. ...- . / --- ..." } },
  { id: "unicode-escape", name: "Unicode Escape", tagline: "Non-ASCII → \\uXXXX and back", category: "Encode & Decode", icon: Type, engine: { kind: "transform", options: [{ key: "mode", label: "Mode", type: "select", choices: ["escape", "unescape"], default: "escape" }], fn: (s, o) => (o.mode === "escape" ? F.unicodeEscape(s) : F.unicodeUnescape(s)), sample: "héllo wörld → 🐝" } },
  { id: "json-escape", name: "JSON String Escape", tagline: "Escape/unescape a JSON string literal", category: "Encode & Decode", icon: Quote, engine: { kind: "transform", options: [{ key: "mode", label: "Mode", type: "select", choices: ["escape", "unescape"], default: "escape" }], fn: (s, o) => { if (o.mode === "escape") return JSON.stringify(s); const v = F.parseJson(s.trim().startsWith('"') ? s : JSON.stringify(s)); if (typeof v !== "string") throw new Error("Input isn't a JSON string literal."); return v; } } },

  /* ── Hash & Crypto ── */
  { id: "sha256", name: "SHA-256 Hash", tagline: "Hex digest via WebCrypto", category: "Hash & Crypto", icon: Fingerprint, engine: { kind: "custom", component: () => <HashTool algo="SHA-256" /> } },
  { id: "sha1", name: "SHA-1 Hash", tagline: "Legacy digest (not for security)", category: "Hash & Crypto", icon: Fingerprint, engine: { kind: "custom", component: () => <HashTool algo="SHA-1" /> } },
  { id: "sha384", name: "SHA-384 Hash", tagline: "Hex digest via WebCrypto", category: "Hash & Crypto", icon: Fingerprint, engine: { kind: "custom", component: () => <HashTool algo="SHA-384" /> } },
  { id: "sha512", name: "SHA-512 Hash", tagline: "Hex digest via WebCrypto", category: "Hash & Crypto", icon: Fingerprint, engine: { kind: "custom", component: () => <HashTool algo="SHA-512" /> } },
  { id: "crc32", name: "CRC32 Checksum", tagline: "Fast integrity checksum", category: "Hash & Crypto", icon: Hash, engine: { kind: "transform", fn: F.crc32 } },
  { id: "hmac", name: "HMAC Generator", tagline: "Keyed signature (SHA family)", category: "Hash & Crypto", icon: KeyRound, engine: { kind: "custom", component: HmacTool } },
  {
    id: "basic-auth", name: "Basic Auth Header", tagline: "user:pass → Authorization header", category: "Hash & Crypto", icon: Lock,
    engine: {
      kind: "fields",
      fields: [
        { key: "user", label: "Username", type: "text", placeholder: "admin" },
        { key: "pass", label: "Password", type: "text", placeholder: "hunter2" },
      ],
      compute: (v) => {
        if (!v.user) throw new Error("Enter a username.");
        return `Authorization: Basic ${F.b64encode(`${v.user}:${v.pass}`)}`;
      },
    },
  },
  { id: "jwt-decoder", name: "JWT Decoder", tagline: "Decode header & payload (no verify)", category: "Hash & Crypto", icon: Shield, engine: { kind: "transform", fn: F.jwtDecode, placeholder: "Paste a JWT (xxx.yyy.zzz)…" } },

  /* ── Generators ── */
  { id: "uuid", name: "UUID Generator", tagline: "RFC 4122 v4 identifiers", category: "Generators", icon: Fingerprint, engine: { kind: "custom", component: () => <RegenList make={F.uuidv4} /> } },
  { id: "ulid", name: "ULID Generator", tagline: "Sortable unique identifiers", category: "Generators", icon: Fingerprint, engine: { kind: "custom", component: () => <RegenList make={F.ulid} /> } },
  { id: "nanoid", name: "Nano ID Generator", tagline: "Compact URL-safe ids", category: "Generators", icon: Fingerprint, engine: { kind: "generate", options: [{ key: "len", label: "Length", type: "number", default: 21, min: 4, max: 64 }, { key: "n", label: "Count", type: "number", default: 5, min: 1, max: 100 }], fn: (o) => Array.from({ length: Number(o.n) }, () => F.nanoid(Number(o.len))).join("\n") } },
  { id: "random-hex", name: "Random Bytes (hex)", tagline: "Secrets & tokens from the CSPRNG", category: "Generators", icon: KeyRound, engine: { kind: "generate", options: [{ key: "bytes", label: "Bytes", type: "number", default: 32, min: 1, max: 256 }], fn: (o) => F.randomHex(Number(o.bytes)) } },
  { id: "password", name: "Password Generator", tagline: "Strong random passwords", category: "Generators", icon: Lock, engine: { kind: "generate", options: [{ key: "len", label: "Length", type: "number", default: 20, min: 6, max: 128 }, { key: "upper", label: "A-Z", type: "checkbox", default: true }, { key: "digits", label: "0-9", type: "checkbox", default: true }, { key: "symbols", label: "!@#", type: "checkbox", default: true }, { key: "n", label: "Count", type: "number", default: 5, min: 1, max: 50 }], fn: (o) => Array.from({ length: Number(o.n) }, () => F.password(Number(o.len), { upper: !!o.upper, digits: !!o.digits, symbols: !!o.symbols })).join("\n") } },
  { id: "passphrase", name: "Passphrase Generator", tagline: "Memorable word-based secrets", category: "Generators", icon: KeyRound, engine: { kind: "generate", options: [{ key: "words", label: "Words", type: "number", default: 4, min: 2, max: 10 }, { key: "n", label: "Count", type: "number", default: 5, min: 1, max: 50 }], fn: (o) => Array.from({ length: Number(o.n) }, () => F.passphrase(Number(o.words))).join("\n") } },
  { id: "fake-person", name: "Fake Person Data", tagline: "Names, emails & phones for tests", category: "Generators", icon: Smile, engine: { kind: "custom", component: () => <RegenList make={F.fakePerson} mono={false} /> } },
  { id: "random-number", name: "Random Number", tagline: "CSPRNG integers in a range", category: "Generators", icon: Dices, engine: { kind: "generate", options: [{ key: "min", label: "Min", type: "number", default: 1 }, { key: "max", label: "Max", type: "number", default: 100 }, { key: "n", label: "Count", type: "number", default: 5, min: 1, max: 100 }], fn: (o) => Array.from({ length: Number(o.n) }, () => String(F.randomInt(Number(o.min), Number(o.max)))).join("\n") } },
  /* ── JSON & Data ── */
  { id: "json-formatter", name: "JSON Formatter", tagline: "Pretty-print with clear errors", category: "JSON & Data", icon: Braces, engine: { kind: "transform", options: [{ key: "indent", label: "Indent", type: "select", choices: ["2 spaces", "4 spaces", "tab"], default: "2 spaces" }], fn: (s, o) => JSON.stringify(F.parseJson(s), null, o.indent === "tab" ? "\t" : o.indent === "4 spaces" ? 4 : 2), sample: '{"name":"hive","apps":[1,2,3],"live":true}' } },
  { id: "json-minifier", name: "JSON Minifier", tagline: "Strip whitespace from JSON", category: "JSON & Data", icon: Braces, engine: { kind: "transform", fn: (s) => JSON.stringify(F.parseJson(s)) } },
  { id: "json-validator", name: "JSON Validator", tagline: "Check syntax, locate errors", category: "JSON & Data", icon: Braces, engine: { kind: "transform", fn: (s) => { const v = F.parseJson(s); const type = Array.isArray(v) ? `array · ${v.length} items` : v === null ? "null" : typeof v === "object" ? `object · ${Object.keys(v as object).length} keys` : typeof v; return `✓ Valid JSON (${type})`; } } },
  { id: "json-sort-keys", name: "JSON Sort Keys", tagline: "Alphabetize keys recursively", category: "JSON & Data", icon: ArrowDownAZ, engine: { kind: "transform", fn: (s) => JSON.stringify(F.sortKeysDeep(F.parseJson(s)), null, 2) } },
  { id: "json-flatten", name: "JSON Flatten", tagline: "Nested JSON → dot-path keys", category: "JSON & Data", icon: Layers, engine: { kind: "transform", fn: (s) => JSON.stringify(F.flattenJson(F.parseJson(s)), null, 2), sample: '{"a":{"b":[1,{"c":true}]}}' } },
  { id: "json-to-yaml", name: "JSON → YAML", tagline: "Convert JSON to YAML", category: "JSON & Data", icon: FileCode, engine: { kind: "transform", fn: (s) => F.jsonToYaml(F.parseJson(s)) } },
  { id: "yaml-to-json", name: "YAML → JSON", tagline: "Convert everyday YAML to JSON", category: "JSON & Data", icon: FileCode, engine: { kind: "transform", fn: (s) => JSON.stringify(F.yamlToJson(s), null, 2), sample: "service: hive\nreplicas: 3\nports:\n  - 8080\n  - 8443\nenv:\n  LOG_LEVEL: debug" } },
  { id: "json-to-csv", name: "JSON → CSV", tagline: "Array of objects → CSV", category: "JSON & Data", icon: Table, engine: { kind: "transform", fn: F.jsonToCsv, sample: '[{"app":"ares","runs":48},{"app":"athena","runs":31}]' } },
  { id: "csv-to-json", name: "CSV → JSON", tagline: "Rows → array of objects", category: "JSON & Data", icon: Table, engine: { kind: "transform", options: [{ key: "sep", label: "Delimiter", type: "select", choices: [",", ";", "tab", "|"], default: "," }, { key: "header", label: "Header row", type: "checkbox", default: true }], fn: (s, o) => F.csvToJson(s, o.sep === "tab" ? "\t" : String(o.sep), !!o.header), sample: "app,runs\nares,48\nathena,31" } },
  { id: "csv-to-markdown", name: "CSV → Markdown Table", tagline: "Rows → GFM table", category: "JSON & Data", icon: Table, engine: { kind: "transform", fn: (s) => F.csvToMarkdown(s), sample: "app,runs\nares,48\nathena,31" } },
  { id: "env-to-json", name: ".env → JSON", tagline: "KEY=value pairs → JSON object", category: "JSON & Data", icon: FileCode, engine: { kind: "transform", fn: F.envToJson, sample: "# hive\nDATABASE_URL=postgres://localhost/hive\nLOG_LEVEL=debug" } },
  { id: "query-string", name: "Query String Parser", tagline: "?a=1&b=2 → JSON", category: "JSON & Data", icon: Link2, engine: { kind: "transform", fn: F.queryStringParse, sample: "https://hive.dev/search?q=hooks&limit=20&tag=a&tag=b" } },
  { id: "lines-to-array", name: "Lines → JSON Array", tagline: "One item per line → array literal", category: "JSON & Data", icon: Brackets, engine: { kind: "transform", fn: (s) => JSON.stringify(F.lines(s).filter((l) => l.trim() !== ""), null, 2) } },
  { id: "sql-in-list", name: "Lines → SQL IN (…)", tagline: "Values → quoted SQL list", category: "JSON & Data", icon: Database, engine: { kind: "transform", fn: (s) => "(" + F.lines(s).filter((l) => l.trim() !== "").map((l) => `'${l.trim().replace(/'/g, "''")}'`).join(", ") + ")", sample: "ares\nathena\ngarlic" } },
  { id: "xml-formatter", name: "XML Formatter", tagline: "Indent XML for reading", category: "JSON & Data", icon: Code2, engine: { kind: "transform", fn: F.xmlFormat, sample: "<pkg><name>hive</name><apps><app>ares</app></apps></pkg>" } },

  /* ── Time & Date ── */
  { id: "timestamp", name: "Timestamp Converter", tagline: "Unix ↔ ISO ↔ local, live clock", category: "Time & Date", icon: Clock, engine: { kind: "custom", component: TimestampTool } },
  { id: "cron", name: "Cron Explainer", tagline: "Describe an expression + next runs", category: "Time & Date", icon: CalendarClock, engine: { kind: "custom", component: CronExplainer } },
  {
    id: "date-diff", name: "Date Difference", tagline: "Time between two moments", category: "Time & Date", icon: CalendarRange,
    engine: {
      kind: "fields",
      fields: [
        { key: "a", label: "From", type: "datetime-local" },
        { key: "b", label: "To", type: "datetime-local" },
      ],
      compute: (v) => {
        const a = new Date(v.a);
        const b = new Date(v.b);
        if (isNaN(a.getTime()) || isNaN(b.getTime())) throw new Error("Pick both dates.");
        const ms = b.getTime() - a.getTime();
        return rowsOut([
          ["Duration", F.humanDuration(ms)],
          ["Days", (ms / 86400000).toFixed(2)],
          ["Hours", (ms / 3600000).toFixed(2)],
          ["Minutes", (ms / 60000).toFixed(1)],
          ["Seconds", String(Math.round(ms / 1000))],
        ]);
      },
    },
  },
  {
    id: "date-add", name: "Date Calculator", tagline: "Add or subtract time from a date", category: "Time & Date", icon: CalendarDays,
    engine: {
      kind: "fields",
      fields: [
        { key: "date", label: "Start", type: "datetime-local" },
        { key: "op", label: "Operation", type: "select", choices: ["add", "subtract"], default: "add" },
        { key: "amount", label: "Amount", type: "number", default: "7" },
        { key: "unit", label: "Unit", type: "select", choices: ["minutes", "hours", "days", "weeks", "months"], default: "days" },
      ],
      compute: (v) => {
        const d = new Date(v.date);
        if (isNaN(d.getTime())) throw new Error("Pick a start date.");
        const n = num(v.amount, "amount") * (v.op === "subtract" ? -1 : 1);
        const out = new Date(d.getTime());
        if (v.unit === "months") out.setMonth(out.getMonth() + n);
        else {
          const ms = { minutes: 60000, hours: 3600000, days: 86400000, weeks: 604800000 }[v.unit]!;
          out.setTime(out.getTime() + n * ms);
        }
        return rowsOut([
          ["Result (local)", out.toLocaleString()],
          ["ISO 8601", out.toISOString()],
          ["Unix", String(Math.floor(out.getTime() / 1000))],
          ["Weekday", out.toLocaleDateString(undefined, { weekday: "long" })],
        ]);
      },
    },
  },
  {
    id: "duration-humanizer", name: "Duration Humanizer", tagline: "8493000 ms → 2h 21m 33s", category: "Time & Date", icon: Hourglass,
    engine: {
      kind: "fields",
      fields: [
        { key: "value", label: "Value", type: "number", default: "90061" },
        { key: "unit", label: "Unit", type: "select", choices: ["milliseconds", "seconds", "minutes"], default: "seconds" },
      ],
      compute: (v) => {
        const mult = { milliseconds: 1, seconds: 1000, minutes: 60000 }[v.unit]!;
        const ms = num(v.value, "value") * mult;
        return rowsOut([
          ["Human", F.humanDuration(ms)],
          ["Milliseconds", ms.toLocaleString()],
          ["Seconds", (ms / 1000).toLocaleString()],
          ["Hours", (ms / 3600000).toFixed(3)],
          ["Days", (ms / 86400000).toFixed(4)],
        ]);
      },
    },
  },
  {
    id: "timezone", name: "Timezone Converter", tagline: "Wall time across zones", category: "Time & Date", icon: Globe,
    engine: {
      kind: "fields",
      fields: [
        { key: "dt", label: "Date & time", type: "datetime-local" },
        { key: "from", label: "From zone", type: "select", choices: TIMEZONES, default: "Local" },
        { key: "to", label: "To zone", type: "select", choices: TIMEZONES, default: "UTC" },
      ],
      compute: (v) => {
        const utc = zonedToUtc(v.dt, v.from);
        const inZone = (zone: string) =>
          zone === "Local"
            ? utc.toLocaleString()
            : utc.toLocaleString("en-GB", { timeZone: zone, dateStyle: "medium", timeStyle: "short" });
        return rowsOut([
          [`In ${v.to}`, inZone(v.to)],
          ["ISO 8601 (UTC)", utc.toISOString()],
          ["Unix", String(Math.floor(utc.getTime() / 1000))],
        ]);
      },
      note: "Interprets the input as wall time in the From zone.",
    },
  },
  {
    id: "week-number", name: "Week Number", tagline: "ISO week, day-of-year, quarter", category: "Time & Date", icon: CalendarDays,
    engine: {
      kind: "fields",
      fields: [{ key: "date", label: "Date", type: "date" }],
      compute: (v) => {
        const d = new Date(v.date + "T12:00:00");
        if (isNaN(d.getTime())) throw new Error("Pick a date.");
        const target = new Date(d.getTime());
        target.setDate(target.getDate() + 3 - ((target.getDay() + 6) % 7));
        const firstThu = new Date(target.getFullYear(), 0, 4);
        const week = 1 + Math.round(((target.getTime() - firstThu.getTime()) / 86400000 - 3 + ((firstThu.getDay() + 6) % 7)) / 7);
        const doy = Math.floor((d.getTime() - new Date(d.getFullYear(), 0, 0).getTime()) / 86400000);
        return rowsOut([
          ["ISO week", `W${String(week).padStart(2, "0")}`],
          ["Day of year", String(doy)],
          ["Quarter", `Q${Math.floor(d.getMonth() / 3) + 1}`],
          ["Weekday", d.toLocaleDateString(undefined, { weekday: "long" })],
          ["Leap year", (d.getFullYear() % 4 === 0 && d.getFullYear() % 100 !== 0) || d.getFullYear() % 400 === 0 ? "yes" : "no"],
        ]);
      },
    },
  },
  {
    id: "age-calculator", name: "Age Calculator", tagline: "Exact age from a birth date", category: "Time & Date", icon: Timer,
    engine: {
      kind: "fields",
      fields: [{ key: "dob", label: "Birth date", type: "date" }],
      compute: (v) => {
        const dob = new Date(v.dob + "T00:00:00");
        if (isNaN(dob.getTime())) throw new Error("Pick a birth date.");
        const now = new Date();
        if (dob > now) throw new Error("Birth date is in the future.");
        let years = now.getFullYear() - dob.getFullYear();
        let months = now.getMonth() - dob.getMonth();
        let days = now.getDate() - dob.getDate();
        if (days < 0) { months--; days += new Date(now.getFullYear(), now.getMonth(), 0).getDate(); }
        if (months < 0) { years--; months += 12; }
        const totalDays = Math.floor((now.getTime() - dob.getTime()) / 86400000);
        return rowsOut([
          ["Age", `${years}y ${months}m ${days}d`],
          ["Total days", totalDays.toLocaleString()],
          ["Total weeks", Math.floor(totalDays / 7).toLocaleString()],
          ["Next birthday", new Date(now.getFullYear() + (now >= new Date(now.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0), dob.getMonth(), dob.getDate()).toLocaleDateString()],
        ]);
      },
    },
  },
  {
    id: "time-units", name: "Time Units Reference", tagline: "Seconds in an hour, TTLs, ms tables", category: "Time & Date", icon: Watch,
    engine: {
      kind: "reference",
      columns: ["Duration", "Seconds", "Milliseconds"],
      rows: [
        ["1 minute", "60", "60,000"],
        ["5 minutes", "300", "300,000"],
        ["15 minutes", "900", "900,000"],
        ["30 minutes", "1,800", "1,800,000"],
        ["1 hour", "3,600", "3,600,000"],
        ["6 hours", "21,600", "21,600,000"],
        ["12 hours", "43,200", "43,200,000"],
        ["1 day", "86,400", "86,400,000"],
        ["1 week", "604,800", "604,800,000"],
        ["30 days", "2,592,000", "2,592,000,000"],
        ["90 days", "7,776,000", "7,776,000,000"],
        ["365 days", "31,536,000", "31,536,000,000"],
      ],
      note: "Handy for cache TTLs, cookie Max-Age and cron sanity checks.",
    },
  },

  /* ── Numbers & Units ── */
  {
    id: "base-converter", name: "Number Base Converter", tagline: "Binary ↔ octal ↔ decimal ↔ hex", category: "Numbers & Units", icon: Binary,
    engine: {
      kind: "fields",
      fields: [
        { key: "value", label: "Value", type: "text", default: "255", placeholder: "ff / 255 / 11111111" },
        { key: "from", label: "From base", type: "select", choices: ["2", "8", "10", "16", "36"], default: "10" },
      ],
      compute: (v) => {
        const from = Number(v.from);
        return rowsOut([
          ["Binary (2)", F.convertBase(v.value, from, 2)],
          ["Octal (8)", F.convertBase(v.value, from, 8)],
          ["Decimal (10)", F.convertBase(v.value, from, 10)],
          ["Hex (16)", F.convertBase(v.value, from, 16)],
          ["Base 36", F.convertBase(v.value, from, 36)],
        ]);
      },
    },
  },
  {
    id: "percentage", name: "Percentage Calculator", tagline: "Of, share and change — all at once", category: "Numbers & Units", icon: Percent,
    engine: {
      kind: "fields",
      fields: [
        { key: "x", label: "X", type: "number", default: "25" },
        { key: "y", label: "Y", type: "number", default: "80" },
      ],
      compute: (v) => {
        const x = num(v.x, "X");
        const y = num(v.y, "Y");
        return rowsOut([
          [`${x}% of ${y}`, String(Math.round(((x / 100) * y + Number.EPSILON) * 10000) / 10000)],
          [`${x} as % of ${y}`, y === 0 ? "∞" : ((x / y) * 100).toFixed(2) + "%"],
          [`Change ${x} → ${y}`, x === 0 ? "∞" : (((y - x) / Math.abs(x)) * 100).toFixed(2) + "%"],
        ]);
      },
    },
  },
  {
    id: "aspect-ratio", name: "Aspect Ratio", tagline: "Simplify ratios, scale dimensions", category: "Numbers & Units", icon: Scale,
    engine: {
      kind: "fields",
      fields: [
        { key: "w", label: "Width", type: "number", default: "1920" },
        { key: "h", label: "Height", type: "number", default: "1080" },
        { key: "tw", label: "Target width (optional)", type: "number", default: "1280" },
      ],
      compute: (v) => {
        const w = num(v.w, "width");
        const h = num(v.h, "height");
        const g = F.gcd(w, h) || 1;
        const rows: [string, string][] = [
          ["Ratio", `${w / g}:${h / g}`],
          ["Decimal", (w / h).toFixed(4)],
        ];
        if (v.tw.trim() !== "") {
          const tw = num(v.tw, "target width");
          rows.push([`Height at ${tw}w`, String(Math.round((tw * h) / w))]);
        }
        return rowsOut(rows);
      },
    },
  },
  {
    id: "byte-size", name: "Byte Size Converter", tagline: "B ↔ KB ↔ MiB ↔ GB…", category: "Numbers & Units", icon: Database,
    engine: {
      kind: "fields",
      fields: [
        { key: "value", label: "Value", type: "number", default: "1" },
        { key: "unit", label: "Unit", type: "select", choices: ["B", "KB", "MB", "GB", "TB", "KiB", "MiB", "GiB", "TiB"], default: "GB" },
      ],
      compute: (v) => {
        const mult: Record<string, number> = { B: 1, KB: 1e3, MB: 1e6, GB: 1e9, TB: 1e12, KiB: 1024, MiB: 1024 ** 2, GiB: 1024 ** 3, TiB: 1024 ** 4 };
        const bytes = num(v.value, "value") * mult[v.unit];
        const fmtN = (n: number) => (n >= 1e15 ? n.toExponential(3) : n.toLocaleString(undefined, { maximumFractionDigits: 4 }));
        return rowsOut([
          ["Bytes", fmtN(bytes)],
          ["KB / MB / GB", `${fmtN(bytes / 1e3)} / ${fmtN(bytes / 1e6)} / ${fmtN(bytes / 1e9)}`],
          ["KiB / MiB / GiB", `${fmtN(bytes / 1024)} / ${fmtN(bytes / 1024 ** 2)} / ${fmtN(bytes / 1024 ** 3)}`],
          ["Bits", fmtN(bytes * 8)],
        ]);
      },
      note: "KB = 1000 bytes (decimal) · KiB = 1024 bytes (binary).",
    },
  },
  {
    id: "transfer-time", name: "Transfer Time", tagline: "How long a download really takes", category: "Numbers & Units", icon: Timer,
    engine: {
      kind: "fields",
      fields: [
        { key: "size", label: "Size", type: "number", default: "2" },
        { key: "unit", label: "Unit", type: "select", choices: ["MB", "GB", "TB"], default: "GB" },
        { key: "speed", label: "Speed (Mbit/s)", type: "number", default: "100" },
      ],
      compute: (v) => {
        const mult: Record<string, number> = { MB: 8e6, GB: 8e9, TB: 8e12 };
        const bits = num(v.size, "size") * mult[v.unit];
        const mbps = num(v.speed, "speed");
        if (mbps <= 0) throw new Error("Speed must be positive.");
        const seconds = bits / (mbps * 1e6);
        return rowsOut([
          ["Duration", F.humanDuration(seconds * 1000)],
          ["Seconds", seconds.toFixed(1)],
          ["Effective MB/s", (mbps / 8).toFixed(1)],
        ]);
      },
    },
  },
  { id: "roman", name: "Roman Numerals", tagline: "42 ↔ XLII (auto-detects direction)", category: "Numbers & Units", icon: Pi, engine: { kind: "transform", fn: (s) => { const t = s.trim(); if (/^\d+$/.test(t)) return F.toRoman(Number(t)); return String(F.fromRoman(t)); }, placeholder: "42 or XLII", sample: "2026" } },
  { id: "number-words", name: "Number → Words", tagline: "1024 → one thousand, twenty-four", category: "Numbers & Units", icon: Type, engine: { kind: "transform", fn: (s) => F.numberToWords(Number(s.trim().replace(/[,_\s]/g, ""))), placeholder: "1024", sample: "1234567" } },
  {
    id: "prime-factors", name: "Prime Factorizer", tagline: "Factor integers, test primality", category: "Numbers & Units", icon: Sigma,
    engine: {
      kind: "fields",
      fields: [{ key: "n", label: "Integer", type: "number", default: "1729" }],
      compute: (v) => {
        const n = num(v.n, "integer");
        const factors = F.primeFactors(n);
        return rowsOut([
          ["Prime?", factors.length === 1 ? "yes" : "no"],
          ["Factors", factors.join(" × ")],
          ["Distinct", [...new Set(factors)].join(", ")],
        ]);
      },
    },
  },
  {
    id: "gcd-lcm", name: "GCD & LCM", tagline: "Greatest divisor, least multiple", category: "Numbers & Units", icon: Calculator,
    engine: {
      kind: "fields",
      fields: [
        { key: "a", label: "A", type: "number", default: "48" },
        { key: "b", label: "B", type: "number", default: "180" },
      ],
      compute: (v) => {
        const a = num(v.a, "A");
        const b = num(v.b, "B");
        const g = F.gcd(a, b);
        return rowsOut([
          ["GCD", String(g)],
          ["LCM", g === 0 ? "0" : String(Math.abs((a / g) * b))],
        ]);
      },
    },
  },
  {
    id: "temperature", name: "Temperature Converter", tagline: "°C ↔ °F ↔ K", category: "Numbers & Units", icon: Thermometer,
    engine: {
      kind: "fields",
      fields: [
        { key: "value", label: "Value", type: "number", default: "100" },
        { key: "unit", label: "Unit", type: "select", choices: ["°C", "°F", "K"], default: "°C" },
      ],
      compute: (v) => {
        const x = num(v.value, "value");
        const c = v.unit === "°C" ? x : v.unit === "°F" ? ((x - 32) * 5) / 9 : x - 273.15;
        return rowsOut([
          ["Celsius", c.toFixed(2) + " °C"],
          ["Fahrenheit", ((c * 9) / 5 + 32).toFixed(2) + " °F"],
          ["Kelvin", (c + 273.15).toFixed(2) + " K"],
        ]);
      },
    },
  },
  {
    id: "length", name: "Length Converter", tagline: "mm · cm · m · km · in · ft · mi", category: "Numbers & Units", icon: Ruler,
    engine: {
      kind: "fields",
      fields: [
        { key: "value", label: "Value", type: "number", default: "1" },
        { key: "unit", label: "Unit", type: "select", choices: ["mm", "cm", "m", "km", "in", "ft", "yd", "mi"], default: "m" },
      ],
      compute: (v) => {
        const meters: Record<string, number> = { mm: 0.001, cm: 0.01, m: 1, km: 1000, in: 0.0254, ft: 0.3048, yd: 0.9144, mi: 1609.344 };
        const m = num(v.value, "value") * meters[v.unit];
        const f = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 6 });
        return rowsOut([
          ["Metric", `${f(m * 1000)} mm · ${f(m * 100)} cm · ${f(m)} m · ${f(m / 1000)} km`],
          ["Imperial", `${f(m / 0.0254)} in · ${f(m / 0.3048)} ft · ${f(m / 0.9144)} yd · ${f(m / 1609.344)} mi`],
        ]);
      },
    },
  },
  {
    id: "weight", name: "Weight Converter", tagline: "g · kg · t · oz · lb · st", category: "Numbers & Units", icon: Scale,
    engine: {
      kind: "fields",
      fields: [
        { key: "value", label: "Value", type: "number", default: "1" },
        { key: "unit", label: "Unit", type: "select", choices: ["g", "kg", "t", "oz", "lb", "st"], default: "kg" },
      ],
      compute: (v) => {
        const grams: Record<string, number> = { g: 1, kg: 1000, t: 1e6, oz: 28.349523, lb: 453.59237, st: 6350.29318 };
        const g = num(v.value, "value") * grams[v.unit];
        const f = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 6 });
        return rowsOut([
          ["Metric", `${f(g)} g · ${f(g / 1000)} kg · ${f(g / 1e6)} t`],
          ["Imperial", `${f(g / 28.349523)} oz · ${f(g / 453.59237)} lb · ${f(g / 6350.29318)} st`],
        ]);
      },
    },
  },

  /* ── Web & Network ── */
  { id: "http-statuses", name: "HTTP Status Codes", tagline: "Every code you'll actually meet", category: "Web & Network", icon: Server, engine: { kind: "reference", columns: ["Code", "Name", "When you see it"], rows: HTTP_STATUSES } },
  { id: "http-headers", name: "HTTP Headers", tagline: "Request & response header reference", category: "Web & Network", icon: Server, engine: { kind: "reference", columns: ["Header", "Direction", "Purpose"], rows: HTTP_HEADERS } },
  { id: "mime-types", name: "MIME Types", tagline: "Extension → Content-Type lookup", category: "Web & Network", icon: FileText, engine: { kind: "reference", columns: ["Extension", "MIME type", "What it is"], rows: MIME_TYPES } },
  { id: "common-ports", name: "Common Ports", tagline: "Who's squatting on that port", category: "Web & Network", icon: Network, engine: { kind: "reference", columns: ["Port", "Service", "Notes"], rows: COMMON_PORTS } },
  { id: "dns-records", name: "DNS Record Types", tagline: "A, CNAME, MX, TXT and friends", category: "Web & Network", icon: Globe, engine: { kind: "reference", columns: ["Type", "Purpose"], rows: DNS_RECORDS } },
  { id: "url-inspector", name: "URL Inspector", tagline: "Break a URL into its parts", category: "Web & Network", icon: Link2, engine: { kind: "transform", fn: F.urlParse, sample: "https://hive.dev:8443/apps/all?tab=utils&sort=name#top" } },
  {
    id: "ip-inspector", name: "IPv4 Inspector", tagline: "Validate, classify, convert", category: "Web & Network", icon: Network,
    engine: {
      kind: "fields",
      fields: [{ key: "ip", label: "IPv4 address", type: "text", default: "192.168.1.100" }],
      compute: (v) => {
        const n = F.parseIpv4(v.ip);
        return rowsOut([
          ["Valid", "yes"],
          ["Scope", F.isPrivateIpv4(n) ? "private / reserved" : "public"],
          ["As integer", String(n)],
          ["As hex", "0x" + n.toString(16).padStart(8, "0")],
          ["Binary", [24, 16, 8, 0].map((s) => ((n >>> s) & 255).toString(2).padStart(8, "0")).join(".")],
        ]);
      },
    },
  },
  {
    id: "cidr", name: "CIDR Calculator", tagline: "Subnet ranges & host counts", category: "Web & Network", icon: Network,
    engine: {
      kind: "fields",
      fields: [{ key: "cidr", label: "CIDR block", type: "text", default: "10.0.0.0/24" }],
      compute: (v) => {
        const info = F.cidrInfo(v.cidr);
        return rowsOut([
          ["Network", info.network],
          ["Netmask", info.mask],
          ["Broadcast", info.broadcast],
          ["Host range", `${info.firstHost} – ${info.lastHost}`],
          ["Addresses", info.total.toLocaleString()],
          ["Usable hosts", info.usable.toLocaleString()],
        ]);
      },
    },
  },
  {
    id: "user-agent", name: "User-Agent Parser", tagline: "Rough browser / OS breakdown", category: "Web & Network", icon: Eye,
    engine: {
      kind: "transform",
      placeholder: "Paste a User-Agent string…",
      sample: typeof navigator !== "undefined" ? navigator.userAgent : "",
      fn: (s) => {
        const ua = s.trim();
        const browser =
          ua.match(/Edg\/([\d.]+)/) ? `Edge ${RegExp.$1}` :
          ua.match(/OPR\/([\d.]+)/) ? `Opera ${RegExp.$1}` :
          ua.match(/Chrome\/([\d.]+)/) ? `Chrome ${RegExp.$1}` :
          ua.match(/Version\/([\d.]+).*Safari/) ? `Safari ${RegExp.$1}` :
          ua.match(/Firefox\/([\d.]+)/) ? `Firefox ${RegExp.$1}` :
          "unknown";
        const os =
          /Windows NT 10/.test(ua) ? "Windows 10/11" :
          /Mac OS X ([\d_]+)/.test(ua) ? `macOS ${RegExp.$1.replace(/_/g, ".")}` :
          /Android ([\d.]+)/.test(ua) ? `Android ${RegExp.$1}` :
          /(iPhone|iPad).*OS ([\d_]+)/.test(ua) ? `iOS ${RegExp.$2.replace(/_/g, ".")}` :
          /Linux/.test(ua) ? "Linux" : "unknown";
        const engine =
          /AppleWebKit/.test(ua) && !/Chrome/.test(ua) ? "WebKit" :
          /Chrome|Chromium|Edg\//.test(ua) ? "Blink" :
          /Gecko\/\d/.test(ua) ? "Gecko" : "unknown";
        const bot = /bot|crawler|spider|curl|wget|python-requests/i.test(ua) ? "likely a bot / script" : "looks like a real browser";
        return `Browser   ${browser}\nOS        ${os}\nEngine    ${engine}\nVerdict   ${bot}`;
      },
    },
  },
  {
    id: "meta-tags", name: "Meta Tag Generator", tagline: "SEO + Open Graph + Twitter cards", category: "Web & Network", icon: Code2,
    engine: {
      kind: "fields",
      fields: [
        { key: "title", label: "Title", type: "text", default: "Hive — the OS for AI delivery" },
        { key: "desc", label: "Description", type: "text", default: "Verification-first software delivery." },
        { key: "url", label: "Canonical URL", type: "text", default: "https://hive.dev" },
        { key: "image", label: "Social image URL", type: "text", default: "https://hive.dev/og.png" },
      ],
      compute: (v) => [
        `<title>${F.htmlEncode(v.title)}</title>`,
        `<meta name="description" content="${F.htmlEncode(v.desc)}" />`,
        `<link rel="canonical" href="${v.url}" />`,
        `<meta property="og:type" content="website" />`,
        `<meta property="og:title" content="${F.htmlEncode(v.title)}" />`,
        `<meta property="og:description" content="${F.htmlEncode(v.desc)}" />`,
        `<meta property="og:url" content="${v.url}" />`,
        `<meta property="og:image" content="${v.image}" />`,
        `<meta name="twitter:card" content="summary_large_image" />`,
        `<meta name="twitter:title" content="${F.htmlEncode(v.title)}" />`,
        `<meta name="twitter:description" content="${F.htmlEncode(v.desc)}" />`,
        `<meta name="twitter:image" content="${v.image}" />`,
      ].join("\n"),
    },
  },
  {
    id: "gitignore", name: ".gitignore Generator", tagline: "Starter ignores per stack", category: "Web & Network", icon: GitBranch,
    engine: {
      kind: "fields",
      fields: [{ key: "stack", label: "Stack", type: "select", choices: Object.keys(GITIGNORE_TEMPLATES), default: "Node" }],
      compute: (v) => `# ${v.stack}\n${GITIGNORE_TEMPLATES[v.stack]}`,
    },
  },
  {
    id: "robots-txt", name: "robots.txt Generator", tagline: "Crawler rules in seconds", category: "Web & Network", icon: FileText,
    engine: {
      kind: "fields",
      fields: [
        { key: "mode", label: "Policy", type: "select", choices: ["allow all", "block all", "block paths"], default: "allow all" },
        { key: "paths", label: "Paths to block (comma-sep)", type: "text", default: "/admin, /api/internal" },
        { key: "sitemap", label: "Sitemap URL (optional)", type: "text", default: "https://hive.dev/sitemap.xml" },
      ],
      compute: (v) => {
        const rules =
          v.mode === "allow all"
            ? "User-agent: *\nAllow: /"
            : v.mode === "block all"
              ? "User-agent: *\nDisallow: /"
              : "User-agent: *\n" + v.paths.split(",").map((p) => `Disallow: ${p.trim()}`).filter((l) => l !== "Disallow:").join("\n");
        return rules + (v.sitemap.trim() ? `\n\nSitemap: ${v.sitemap.trim()}` : "");
      },
    },
  },
  {
    id: "curl-builder", name: "cURL Builder", tagline: "Compose a request command", category: "Web & Network", icon: Terminal,
    engine: {
      kind: "fields",
      fields: [
        { key: "method", label: "Method", type: "select", choices: ["GET", "POST", "PUT", "PATCH", "DELETE"], default: "POST" },
        { key: "url", label: "URL", type: "text", default: "https://api.hive.dev/v1/workflows" },
        { key: "auth", label: "Bearer token (optional)", type: "text" },
        { key: "body", label: "JSON body (optional)", type: "textarea", default: '{"name": "test"}' },
      ],
      compute: (v) => {
        const parts = [`curl -X ${v.method} '${v.url}'`];
        if (v.auth.trim()) parts.push(`  -H 'Authorization: Bearer ${v.auth.trim()}'`);
        if (v.body.trim() && v.method !== "GET") {
          parts.push(`  -H 'Content-Type: application/json'`);
          parts.push(`  -d '${v.body.replace(/'/g, `'\\''`)}'`);
        }
        return parts.join(" \\\n");
      },
    },
  },

  /* ── Design & CSS ── */
  { id: "color-converter", name: "Color Converter", tagline: "hex ↔ rgb ↔ hsl with preview", category: "Design & CSS", icon: Droplet, engine: { kind: "custom", component: ColorConverter } },
  { id: "contrast-checker", name: "Contrast Checker", tagline: "WCAG AA / AAA ratios", category: "Design & CSS", icon: Contrast, engine: { kind: "custom", component: ContrastChecker } },
  { id: "shades", name: "Shades Generator", tagline: "50→900 scale from one color", category: "Design & CSS", icon: Layers, engine: { kind: "custom", component: ShadesGenerator } },
  { id: "palette", name: "Palette Generator", tagline: "Harmonious 5-color schemes", category: "Design & CSS", icon: Palette, engine: { kind: "custom", component: PaletteGenerator } },
  { id: "gradient", name: "Gradient Generator", tagline: "CSS linear gradients, live", category: "Design & CSS", icon: Paintbrush, engine: { kind: "custom", component: GradientGenerator } },
  { id: "box-shadow", name: "Box Shadow Generator", tagline: "Dial in a shadow visually", category: "Design & CSS", icon: Box, engine: { kind: "custom", component: BoxShadowGenerator } },
  {
    id: "px-rem", name: "px ↔ rem Converter", tagline: "Root-relative sizing math", category: "Design & CSS", icon: Ruler,
    engine: {
      kind: "fields",
      fields: [
        { key: "px", label: "Pixels", type: "number", default: "18" },
        { key: "base", label: "Root font size", type: "number", default: "16" },
      ],
      compute: (v) => {
        const px = num(v.px, "pixels");
        const base = num(v.base, "root size");
        if (base <= 0) throw new Error("Root size must be positive.");
        return rowsOut([
          ["rem", (px / base).toFixed(4).replace(/\.?0+$/, "") + "rem"],
          ["em (same context)", (px / base).toFixed(4).replace(/\.?0+$/, "") + "em"],
          ["% of root", ((px / base) * 100).toFixed(1) + "%"],
        ]);
      },
    },
  },
  { id: "tailwind-colors", name: "Tailwind Palette", tagline: "Default 500-shade hex values", category: "Design & CSS", icon: Palette, engine: { kind: "reference", columns: ["Family", "500 hex"], rows: TAILWIND_COLORS, note: "The workhorse 500 shade of every default Tailwind family — use the Shades Generator to expand any of them." } },
  { id: "font-stacks", name: "Font Stack Reference", tagline: "Battle-tested CSS font stacks", category: "Design & CSS", icon: Type, engine: { kind: "reference", columns: ["Style", "font-family stack"], rows: FONT_STACKS } },
  { id: "unicode-inspector", name: "Unicode Inspector", tagline: "Codepoints & UTF-8 bytes per char", category: "Design & CSS", icon: Type, engine: { kind: "transform", fn: F.unicodeInspect, sample: "Héllo 🐝" } },
  {
    id: "svg-data-uri", name: "SVG → Data URI", tagline: "Inline SVG for CSS backgrounds", category: "Design & CSS", icon: SquareCode,
    engine: {
      kind: "transform",
      sample: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 10"><circle cx="5" cy="5" r="4" fill="#f59e0b"/></svg>`,
      fn: (s) => {
        if (!s.trim().startsWith("<svg")) throw new Error("Paste an <svg> element.");
        const encoded = encodeURIComponent(s.replace(/\s+/g, " ").trim())
          .replace(/%20/g, " ")
          .replace(/%3D/g, "=")
          .replace(/%3A/g, ":")
          .replace(/%2F/g, "/");
        return `background-image: url("data:image/svg+xml,${encoded}");`;
      },
    },
  },
  { id: "keycode", name: "Keycode Inspector", tagline: "event.key & event.code live", category: "Design & CSS", icon: Keyboard, engine: { kind: "custom", component: KeycodeInspector } },

  /* ── Dev Tools ── */
  { id: "regex-tester", name: "Regex Tester", tagline: "Live matches, groups & flags", category: "Dev Tools", icon: Regex, engine: { kind: "custom", component: RegexTester } },
  { id: "diff", name: "Diff Viewer", tagline: "Line-by-line text comparison", category: "Dev Tools", icon: FileDiff, engine: { kind: "custom", component: DiffViewer } },
  { id: "markdown-preview", name: "Markdown Preview", tagline: "Render GFM-ish markdown safely", category: "Dev Tools", icon: FileText, engine: { kind: "custom", component: MarkdownPreview } },
  { id: "html-preview", name: "HTML Preview", tagline: "Sandboxed iframe rendering", category: "Dev Tools", icon: Eye, engine: { kind: "custom", component: HtmlPreview } },
  {
    id: "chmod", name: "chmod Calculator", tagline: "755 ↔ rwxr-xr-x", category: "Dev Tools", icon: Terminal,
    engine: {
      kind: "fields",
      fields: [{ key: "mode", label: "Octal (755) or symbolic (rwxr-xr-x)", type: "text", default: "755" }],
      compute: (v) => {
        const s = v.mode.trim();
        let bits: number;
        if (/^[0-7]{3,4}$/.test(s)) {
          bits = parseInt(s.slice(-3), 8);
        } else if (/^[rwx-]{9}$/.test(s)) {
          bits = [...s].reduce((acc, c, i) => (c === "-" ? acc : acc | (1 << (8 - i))), 0);
        } else {
          throw new Error("Enter octal like 644 or symbolic like rw-r--r--.");
        }
        const sym = [8, 7, 6, 5, 4, 3, 2, 1, 0]
          .map((b, i) => ((bits >> b) & 1 ? "rwx"[i % 3] : "-"))
          .join("");
        const who = (shift: number) => {
          const p = (bits >> shift) & 7;
          return [p & 4 && "read", p & 2 && "write", p & 1 && "execute"].filter(Boolean).join(", ") || "none";
        };
        return rowsOut([
          ["Octal", (bits >> 6).toString() + ((bits >> 3) & 7).toString() + (bits & 7).toString()],
          ["Symbolic", sym],
          ["Owner", who(6)],
          ["Group", who(3)],
          ["Others", who(0)],
          ["Usage", `chmod ${(bits >> 6) & 7}${(bits >> 3) & 7}${bits & 7} file`],
        ]);
      },
    },
  },

  /* ── Reference ── */
  { id: "regex-cheatsheet", name: "Regex Cheatsheet", tagline: "Syntax you always re-google", category: "Reference", icon: Regex, engine: { kind: "reference", columns: ["Pattern", "Meaning"], rows: REGEX_CHEATSHEET } },
  { id: "git-cheatsheet", name: "Git Cheatsheet", tagline: "Daily driver → rescue commands", category: "Reference", icon: GitBranch, engine: { kind: "reference", columns: ["Command", "What it does"], rows: GIT_CHEATSHEET } },
  { id: "npm-commands", name: "npm / yarn / pnpm", tagline: "Command translation table", category: "Reference", icon: Package, engine: { kind: "reference", columns: ["npm", "yarn", "pnpm"], rows: NPM_COMMANDS } },
  { id: "docker-cheatsheet", name: "Docker Cheatsheet", tagline: "Containers, images & compose", category: "Reference", icon: Package, engine: { kind: "reference", columns: ["Command", "What it does"], rows: DOCKER_CHEATSHEET } },
  { id: "bash-shortcuts", name: "Shell Shortcuts", tagline: "Readline & bash muscle memory", category: "Reference", icon: Terminal, engine: { kind: "reference", columns: ["Keys", "Action"], rows: BASH_SHORTCUTS } },
  { id: "markdown-cheatsheet", name: "Markdown Cheatsheet", tagline: "GFM syntax at a glance", category: "Reference", icon: ScrollText, engine: { kind: "reference", columns: ["Syntax", "Result"], rows: MARKDOWN_CHEATSHEET } },
  { id: "ascii-table", name: "ASCII Table", tagline: "Printable characters 32–126", category: "Reference", icon: Table, engine: { kind: "reference", columns: ["Dec", "Hex", "Char"], rows: ASCII_ROWS } },
  { id: "html-entities-ref", name: "HTML Entities", tagline: "Named entities worth memorizing", category: "Reference", icon: Code2, engine: { kind: "reference", columns: ["Entity", "Renders", "Name"], rows: HTML_ENTITIES_REF } },
  { id: "semver", name: "Semver Ranges", tagline: "What ^ and ~ actually pin", category: "Reference", icon: Package, engine: { kind: "reference", columns: ["Range", "Meaning"], rows: SEMVER_REF } },
  { id: "licenses", name: "OSS License Guide", tagline: "Permissive vs copyleft at a glance", category: "Reference", icon: BookOpen, engine: { kind: "reference", columns: ["License", "Family", "Gist"], rows: LICENSE_REF, note: "A cheat sheet, not legal advice." } },
  { id: "emoji-ref", name: "Emoji Shortcodes", tagline: "Slack/GitHub codes for standups", category: "Reference", icon: Smile, engine: { kind: "reference", columns: ["Emoji", "Shortcode", "Typical use"], rows: EMOJI_REF } },
  { id: "prompt-starters", name: "Prompt Starters", tagline: "Reusable prompts for agent tasks", category: "Reference", icon: Sparkles, engine: { kind: "reference", columns: ["Task", "Prompt skeleton"], rows: [
    ["Refine a ticket", "Rewrite this ticket with: goal, acceptance criteria, out-of-scope, edge cases: <paste>"],
    ["Review a diff", "Review this diff for correctness bugs only; rank by severity; cite line numbers: <diff>"],
    ["Write tests", "Given this function, write table-driven tests covering happy path, edge cases, and one failure mode: <code>"],
    ["Explain an error", "Explain this stack trace, most likely root cause first, with one fix per cause: <trace>"],
    ["Summarize a thread", "Summarize decisions, open questions, and owners from this thread in ≤5 bullets: <thread>"],
    ["Draft release notes", "Turn these merged PR titles into user-facing release notes grouped by feature/fix: <list>"],
    ["Plan a migration", "Propose a phased migration plan with rollback points and blast-radius notes: <context>"],
    ["Name things", "Suggest 10 names for <thing>; constraints: short, kebab-case-safe, no trademarks."],
  ] } },
];

/** Utilities grouped by category, in display order. */
export const UTILITIES_BY_CATEGORY: { category: string; items: UtilityDef[] }[] =
  UTILITY_CATEGORIES.map((category) => ({
    category,
    items: UTILITIES.filter((u) => u.category === category),
  })).filter((g) => g.items.length > 0);

export function utilityById(id: string): UtilityDef | undefined {
  return UTILITIES.find((u) => u.id === id);
}

/** Accent color per category — one hue per family keeps the grid readable. */
export const CATEGORY_ACCENTS: Record<string, string> = {
  Text: "#f59e0b",
  "Encode & Decode": "#8b5cf6",
  "Hash & Crypto": "#f43f5e",
  Generators: "#10b981",
  "JSON & Data": "#3b82f6",
  "Time & Date": "#06b6d4",
  "Numbers & Units": "#eab308",
  "Web & Network": "#14b8a6",
  "Design & CSS": "#ec4899",
  "Dev Tools": "#f97316",
  Reference: "#94a3b8",
};
